import React from 'react'
import { ApolloClient, ApolloLink, Observable, from } from '@apollo/client'
import { InMemoryCache } from '@apollo/client/cache'
import { onError } from '@apollo/client/link/error'
import { T } from '@transifex/react'
import { createUploadLink } from 'apollo-upload-client'
import { OperationDefinitionNode } from 'graphql'
import { message } from 'src/antd'

import { baseUrl } from 'src/api/baseUrl'
import { refreshAccessToken } from 'src/lib/api-client'
import TokenStorage from 'src/utils/tokenStorage'

import introspectionToPossibleTypes from './cache'
import introspectionQueryResultData from './fragmentTypes.json'

const errorMsg401 = 'You do not have permission to perform this action'
const IAM_TOKEN_INVALID_CODE = 'IAM_TOKEN_INVALID'
const NEW_ACCESS_TOKEN_HEADER = 'x-iam-new-access-token'

const httpLink = createUploadLink({
  uri: `${baseUrl}/graph-api/`,
  // Include credentials (cookies) with requests for IAM refresh token
  credentials: 'include',
}) as unknown

const authMiddleware = new ApolloLink((operation, forward) => {
  const iamToken = TokenStorage.getIAMAccessToken()

  operation.setContext({
    headers: {
      ...(iamToken && { 'X-IAM-Token': iamToken }),
    },
  })

  return forward(operation)
})

/**
 * Error handling link for authentication failures.
 *
 * Handles:
 * - 401 network errors
 * - Permission denied GraphQL errors
 * - IAM_TOKEN_INVALID errors (triggers logout when action='logout')
 */
const logoutLink = onError(
  ({ networkError, graphQLErrors, operation, forward }) => {
    const isRetried = operation.getContext().iamRetried
    const iamError = graphQLErrors?.find(
      (error) =>
        (error.extensions as { code?: string })?.code === IAM_TOKEN_INVALID_CODE
    )
    const is401 = !!(
      networkError && (networkError as $TSFixMe).statusCode === 401
    )

    // Refresh and retry once on IAM token errors or 401s
    if ((iamError || is401) && !isRetried) {
      return new Observable((observer) => {
        refreshAccessToken()
          .then((newToken) => {
            if (!newToken) {
              observer.error(iamError ?? networkError)
              return
            }
            operation.setContext(
              ({ headers }: { headers: Record<string, string> }) => ({
                headers: { ...headers, 'X-IAM-Token': newToken },
                iamRetried: true,
              })
            )
            forward(operation).subscribe({
              next: observer.next.bind(observer),
              error: observer.error.bind(observer),
              complete: observer.complete.bind(observer),
            })
          })
          .catch((err) => {
            observer.error(err)
          })
      })
    }

    // Unrecoverable — bail out to login
    if (
      window.location.pathname !== '/login' &&
      (is401 ||
        (graphQLErrors?.length &&
          graphQLErrors.find((error) => error.message === errorMsg401)))
    ) {
      window.location.href = '/login'
    }

    return undefined
  }
)

/**
 * Afterware link to intercept responses and update IAM token from headers.
 *
 * When the backend refreshes the IAM token, it sends the new access token
 * in the X-IAM-New-Access-Token response header. This link reads that header
 * and updates the token in sessionStorage.
 */
const iamTokenRefreshLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    // Get the response context which contains headers
    const context = operation.getContext()
    const headers = context.response?.headers

    if (headers) {
      // Check for new IAM access token from backend token refresh
      const newAccessToken = headers.get(NEW_ACCESS_TOKEN_HEADER)

      if (newAccessToken) {
        TokenStorage.setIAMAccessToken(newAccessToken)
      }
    }

    return response
  })
})

const requestFailedLink = onError(
  ({ networkError, graphQLErrors, operation }) => {
    const isQuery =
      (
        operation.query.definitions.find(
          (definition) => definition.kind === 'OperationDefinition'
        ) as OperationDefinitionNode
      )?.operation === 'query'

    // showNetworkError would be False by default for mutations and True for queries since in most mutations we already show a message
    const { showNetworkError = isQuery, showGraphqlError = isQuery } =
      operation.getContext()

    if (showNetworkError && networkError?.message) {
      message.error(
        networkError.message || (
          <T _str="Something went wrong (Network error)" />
        )
      )
    }
    if (showGraphqlError && graphQLErrors?.length) {
      graphQLErrors.forEach((error) => {
        message.error(
          <T
            _str="Something went wrong (GraphQL error: { path } query)"
            path={error.path?.join(', ')}
          />
        )
      })
    }
  }
)

const cache = new InMemoryCache({
  possibleTypes: introspectionToPossibleTypes(introspectionQueryResultData),
})

const link = httpLink as ApolloLink

const apolloClient = new ApolloClient({
  // Link chain order:
  // 1. logoutLink - handles auth errors and IAM token invalid
  // 2. iamTokenRefreshLink - intercepts responses for new IAM tokens
  // 3. authMiddleware - adds JWT and IAM token headers
  // 4. link - the actual HTTP/WS transport
  link: from([logoutLink, iamTokenRefreshLink, authMiddleware, link]),
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
    },
  },
})

export default apolloClient
